import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import getEnvConfig from 'utils/getEnvConfig'

const env = getEnvConfig()

let userManager: UserManager | null = null
// sync cache so the token can be read per request without awaiting the user store
let currentUser: User | null = null

export const getUserManager = (): UserManager => {
    if (!userManager) {
        userManager = new UserManager({
            authority: env.Auth.Issuer,
            client_id: env.Auth.ClientID,
            // the go file server only serves index.html on '/', and the callback query
            // params must arrive before the HashRouter fragment
            redirect_uri: window.location.origin + '/',
            post_logout_redirect_uri: window.location.origin + '/',
            // offline_access makes authentik issue a refresh token for the silent renew
            scope: 'openid profile email offline_access',
            automaticSilentRenew: true,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
        })
        userManager.events.addUserLoaded((user) => {
            currentUser = user
        })
        userManager.events.addSilentRenewError((error) => {
            console.warn('Silent token renew failed:', error)
        })
        userManager.events.addAccessTokenExpired(() => {
            // renew failed for good - a redirect is transparent while the IdP session is alive
            getUserManager()
                .signinRedirect({ state: window.location.hash })
                .catch((error) => console.error('Re-login redirect failed:', error))
        })
    }
    return userManager
}

export const getIdToken = (): string | undefined => currentUser?.id_token

// 'login-required' equivalent: resolves with a signed-in user or navigates away to the IdP
export const ensureSignedIn = async (): Promise<User> => {
    const manager = getUserManager()

    const params = new URLSearchParams(window.location.search)
    if (params.has('error')) {
        throw new Error(
            `Login failed at the identity provider: ${params.get('error_description') || params.get('error')}`,
        )
    }
    if (params.has('code') && params.has('state')) {
        const user = await manager.signinRedirectCallback()
        // strip ?code&state and restore the pre-login hash route stashed in state
        const hash = typeof user.state === 'string' && user.state ? user.state : window.location.hash
        window.history.replaceState({}, document.title, window.location.pathname + hash)
        currentUser = user
        return user
    }

    const existingUser = await manager.getUser()
    if (existingUser && !existingUser.expired) {
        currentUser = existingUser
        return existingUser
    }

    await manager.signinRedirect({ state: window.location.hash })
    // the browser is navigating to the IdP - never resolve
    return new Promise<User>(() => undefined)
}

export const signoutOIDC = async (): Promise<void> => {
    currentUser = null
    await getUserManager().signoutRedirect()
}
